import React, { useState, useEffect, useCallback, useMemo, Fragment } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { withTranslate } from 'react-redux-multilingual'

import {
  fetchGrades,
  deleteGrade,
  fetchGrade,
  updateGrade,
  createGrade,
  clearSelectedGradeRecord
} from '../../../../store/gradeCriteria/actions'
import {
  fetchAppraisalTypeOptions,
  fetchCompanyOptions,
  fetchLevelGroupOptions,
  fetchGradeOptions
} from '../../../../store/actions'
import {
  getSelectvalue,
  updateObject,
  updateFormOptions,
  updateFormData,
  updateFilterOptions,
  updateFilterData,
  extractFilterOptions,
  applyRecordDataToForm,
  extractFormData,
  validateForm,
  clearInputForm,
  isTrulyEqual
} from '../../../../helpers/utils'

import { Add, Delete, Edit } from '@material-ui/icons'

import GradeCriteriaContent from '../../../../components/PageContent/SetupData/GradeCriteria/GradeCriteriaContent/GradeCriteriaContent'
import ConfirmationModal from '../../../../components/ConfirmationModal/ConfirmationModal'
import Alert from '../../../../components/Alert/Alert'
import FilterModal from '../../../../components/FilterModal/FilterModal'
import GradeCriteriaModal from '../../../../components/PageContent/SetupData/GradeCriteria/GradeCriteriaModal/GradeCriteriaModal'
import { sumBy } from 'lodash'

const initialForm = {
  appraisal_type_id: {
    label: 'appraisalType',
    elementType: 'newselect',
    elementConfig: {
      options: []
    },
    value: null,
    valid: false,
    touched: false,
    error: null
  },
  grade_criteria_name: {
    label: 'gradeCriteriaName',
    elementType: 'input',
    elementConfig: {
      options: []
    },
    value: [],
    validation: { required: true },
    valid: false,
    touched: false,
    error: null
  },
  company_id: {
    label: 'company',
    elementType: 'newselect',
    elementConfig: {
      options: []
    },
    value: null,
    validation: { required: true },
    valid: false,
    touched: false,
    error: null
  },
  effective_date: {
    label: 'effectiveDate',
    elementType: 'datePicker',
    elementConfig: {},
    value: null,
    valid: false,
    validation: { required: true },
    touched: false,
    error: null
  }
}

const initAdditionalForm = {
  level_group_id: {
    label: 'levelGroup',
    elementType: 'newselect',
    elementConfig: {
      options: [],
      variant: 'outlined',
      size: 'small'
    },
    value: null,
    valid: false,
    validation: {},
    touched: false,
    error: null
  }
}

const gradeLevelRowTemplate = {
  grade_id: {
    elementType: 'hidden',
    value: null,
    valid: true
  },
  grade_name: {
    elementType: 'static',
    value: null,
    valid: true
  },
  grade_level_id: {
    elementType: 'hidden',
    value: null,
    valid: true,
    hidden: true
  },
  level_group_id: {
    elementType: 'hidden',
    value: null,
    valid: true,
    hidden: true
  },
  grade_criteria_id: {
    elementType: 'hidden',
    value: null,
    valid: true,
    hidden: true
  },
  begin_score: {
    label: '',
    elementType: 'input',
    // elementConfig: { disabled: true },
    elementConfig: { type: 'number' },
    value: 0,
    validation: { required: false, isNumeric: true },
    valid: true,
    touched: false,
    error: null
  },
  end_score: {
    label: '',
    elementType: 'input',
    elementConfig: { type: 'number' },
    // elementConfig: { disabled: true },
    value: 0,
    validation: { required: false, isNumeric: true },
    valid: true,
    touched: false,
    error: null
  },
  raise_percent: {
    label: '',
    elementType: 'input',
    elementConfig: { type: 'number', min: 0 },
    // elementConfig: { disabled: true },
    value: 0,
    validation: { required: false, isNumeric: true, min: 0 },
    valid: true,
    touched: false,
    error: null
  },
  grade_budget: {
    label: '',
    elementType: 'input',
    elementConfig: { type: 'number', min: 0, max: 100 },
    // elementConfig: { disabled: true },
    value: 0,
    validation: { required: false, isNumeric: true, min: 0, max: 100 },
    valid: true,
    touched: false,
    error: null
  },
  bonus_month: {
    label: '',
    elementType: 'input',
    elementConfig: { type: 'number', min: 0 },
    // elementConfig: { disabled: true },
    value: 0,
    validation: { required: false, isNumeric: true, min: 0 },
    valid: true,
    touched: false,
    error: null
  }
}

const initialFilter = {
  appraisal_type_id: {
    label: 'appraisalType',
    configs: {
      options: []
    },
    value: null
  },
  company_id: {
    label: 'company',
    configs: {
      options: []
    },
    value: null
  }
}

const filterLabelMapper = {
  appraisal_type_id: {
    itemKey: 'value_list_id',
    itemValue: 'value_list_id',
    itemLabel: { code: 'value_list_code', name: 'value_list_name' }
  },
  company_id: {
    itemKey: 'company_id',
    itemValue: 'company_id',
    itemLabel: { code: 'company_code', name: 'company_name' }
  },
  level_group_id: {
    itemKey: 'level_group_id',
    itemValue: 'level_group_id',
    itemLabel: 'level_group_name'
  }
}

function Grade({ pagePermission: permission, translate, isAdmin, ...rest }) {
  // Get Dispatch function
  const dispatch = useDispatch()

  // Get Initial State
  const [form, setForm] = useState({ ...initialForm })
  const [additionalForm, setAdditionalForm] = useState({ ...initAdditionalForm })
  const [gradeLevelRows, setGradeLevelRows] = useState({})
  const [filter, setFilter] = useState({ ...initialFilter })
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editTargetId, setEditTargetId] = useState(null)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [deleteTargetId, setDeleteTargetId] = useState(null)
  const [editLevelGroupLoaded, setEditLevelGroupLoaded] = useState(false)
  const [showPercentError, setShowPercentError] = useState(false)

  // Get Redux State
  const appraisalTypeOptions = useSelector(state => state.gradeCriteria.appraisalTypes)
  const companyOptions = useSelector(state => state.gradeCriteria.companies)
  const levelGroupOptions = useSelector(state => state.gradeCriteria.levelGroups)
  const gradeOptions = useSelector(state => state.gradeCriteria.grades)
  const loadingQueue = useSelector(state => state.gradeCriteria.loadingStates)
  const pagination = useSelector(state => state.gradeCriteria.pagination)
  const gradeList = useSelector(state => state.gradeCriteria.records)
  const currentFilter = useSelector(state => state.gradeCriteria.filters)
  const selectedRecord = useSelector(state => state.gradeCriteria.selectedRecord)
  const updateSuccess = useSelector(state => state.gradeCriteria.updateSuccess)

  const validateGradeRows = useCallback(gradeLevelRows => {
    const isValid = Object.values(gradeLevelRows).reduce((isValid, rows) => {
      const sumGradeBudget = sumBy(rows, row => +row.grade_budget.value || 0)
      // console.log(gradeLevelRows, sumGradeBudget)
      const weightValid = isTrulyEqual(sumGradeBudget, 100)
      const groupRowsValid = rows.every(row => validateForm(row, { onlyTouched: true }))

      return isValid && groupRowsValid && weightValid
    }, true)
    return isValid
  }, [])

  // Validator functions and variables
  const validateInput = useCallback(
    (form, additionalForm, gradeLevelRows) => {
      const primaryFormIsValid = validateForm(form)
      const additionalFormIsValid = validateForm(additionalForm)
      const allLevelRowsValid = validateGradeRows(gradeLevelRows)

      return { primaryFormIsValid, additionalFormIsValid, allLevelRowsValid }
    },
    [validateGradeRows]
  )

  const { primaryFormIsValid, additionalFormIsValid, allLevelRowsValid } = useMemo(() => {
    return validateInput(form, additionalForm, gradeLevelRows)
  }, [form, additionalForm, gradeLevelRows, validateInput])

  const currentLevelGroupId = additionalForm.level_group_id.value?.value || 0

  const formIsValid = primaryFormIsValid && additionalFormIsValid && allLevelRowsValid

  // Handler functions
  const openEditModalHandler = useCallback(
    (type, targetId) => {
      dispatch(fetchGradeOptions())
      switch (type) {
        case 'add':
          setEditModalOpen(true)
          break
        case 'edit':
          if (targetId) {
            setEditModalOpen(true)
            setEditTargetId(targetId)
            dispatch(fetchGrade(targetId))
          }
          break

        default:
          break
      }
    },
    [dispatch]
  )

  const formSubmittedHandler = useCallback(() => {
    const gradeCriteriaData = extractFormData(form)
    const gradeLevelData = Object.entries(gradeLevelRows).reduce((extracted, levelGroup) => {
      const [levelGroupId, gradeRows] = levelGroup
      const extractedGradeRows = gradeRows.reduce((extractedRow, row) => {
        const data = extractFormData(row)
        const { begin_score, end_score, raise_percent, grade_budget, bonus_month } = data
        if (
          begin_score !== '' &&
          end_score !== '' &&
          raise_percent !== '' &&
          grade_budget !== '' &&
          bonus_month !== ''
        ) {
          return [...extractedRow, data]
        }
        return extractedRow
      }, [])

      if (extractedGradeRows.length) {
        return { ...extracted, [levelGroupId]: extractedGradeRows }
      }
      return extracted
    }, {})

    const data = {
      grade_criteria: gradeCriteriaData,
      grade_level: gradeLevelData
    }
    if (editTargetId) {
      dispatch(updateGrade(editTargetId, data))
    } else {
      dispatch(createGrade(data))
    }
  }, [dispatch, editTargetId, form, gradeLevelRows])

  // Form options changed handler
  const formOptionChangedHandler = useCallback((itemKey, newOptions, { key, value, label }) => {
    setForm(prevControls => {
      const formItem = prevControls[itemKey]

      if (!formItem) {
        return prevControls
      }

      let updatedForm = updateFormOptions(newOptions, formItem, key, value, label)

      if (itemKey === 'level_group_id') {
        updatedForm = { ...updatedForm, value: updatedForm.elementConfig.options[0] || null }
      }
      return updateObject(prevControls, { [itemKey]: updatedForm })
    })
  }, [])

  // Initiator for grade level rows
  const initiateGradeLevel = useCallback(
    levelGroupId => {
      setGradeLevelRows(prevState => {
        let updatedData = { ...prevState }
        if (!prevState[levelGroupId]) {
          const newData = gradeOptions.map(grade => {
            return {
              ...gradeLevelRowTemplate,
              grade_id: { ...gradeLevelRowTemplate.grade_id, value: grade.value_list_id },
              grade_name: { ...gradeLevelRowTemplate.grade_name, value: grade.value_list_name },
              level_group_id: { ...gradeLevelRowTemplate.level_group_id, value: levelGroupId }
            }
          })
          updatedData = updateObject(prevState, { [levelGroupId]: newData })
        }
        return updatedData
      })
    },
    [setGradeLevelRows, gradeOptions]
  )

  // Additional form options changed handler
  const additionalFormOptionChangedHandler = useCallback(
    (itemKey, newOptions, { key, value, label }) => {
      setAdditionalForm(prevControls => {
        const formItem = prevControls[itemKey]

        if (!formItem) {
          return prevControls
        }

        let updatedForm = updateFormOptions(newOptions, formItem, key, value, label)

        if (itemKey === 'level_group_id') {
          const selected = updatedForm.elementConfig.options[0] ?? null
          const levelGroupId = selected?.value
          updatedForm = { ...updatedForm, value: selected }

          if (levelGroupId) {
            initiateGradeLevel(levelGroupId)
          }
        }
        return updateObject(prevControls, { [itemKey]: updatedForm })
      })
    },
    [initiateGradeLevel]
  )

  const clearEditModalData = useCallback(() => {
    setForm(prevForm => {
      return clearInputForm(prevForm)
    })
    setAdditionalForm(prevForm => {
      return clearInputForm(prevForm)
    })
    setGradeLevelRows({})
    setEditTargetId(null)
    dispatch(clearSelectedGradeRecord())

    // Clear level group
    const optionConfig = filterLabelMapper.level_group_id
    const optionMapper = {
      key: optionConfig.itemKey,
      value: optionConfig.itemValue,
      label: optionConfig.itemLabel
    }
    additionalFormOptionChangedHandler('level_group_id', [], optionMapper)
  }, [dispatch, additionalFormOptionChangedHandler])

  const closeEditModalHandler = useCallback(() => {
    clearEditModalData()
    setEditModalOpen(false)
    setEditLevelGroupLoaded(false)
  }, [clearEditModalData])

  // Form input changed handler
  const formInputChangedHandler = useCallback(
    (itemKey, event) => {
      setForm(prevState => {
        const formItem = prevState[itemKey]
        const updatedFormItem = updateFormData(formItem, event)
        const updatedForm = updateObject(prevState, { [itemKey]: updatedFormItem })
        const { appraisal_type_id, company_id } = extractFormData(updatedForm)

        if (itemKey === 'appraisal_type_id') {
          if (appraisal_type_id) {
            dispatch(fetchCompanyOptions({ appraisal_type_id }))
          }
        }
        if (itemKey === 'appraisal_type_id' || itemKey === 'company_id') {
          if (appraisal_type_id && company_id) {
            dispatch(fetchLevelGroupOptions({ appraisal_type_id, company_id }))
            setGradeLevelRows({})
            setAdditionalForm(prevState => {
              const targetForm = prevState.level_group_id
              return updateObject(prevState, {
                level_group_id: {
                  ...targetForm,
                  elementConfig: { ...targetForm.elementConfig, disabled: false }
                }
              })
            })
          }
        }
        return updatedForm
      })
    },
    [dispatch]
  )

  const additionalFormInputChangedHandler = useCallback(
    (itemKey, event) => {
      setAdditionalForm(prevState => {
        const formItem = prevState[itemKey]
        let updatedForm = updateFormData(formItem, event)

        if (itemKey === 'level_group_id') {
          const levelGroupId = updatedForm.value?.value || 0

          if (levelGroupId) {
            initiateGradeLevel(levelGroupId)
          }
        }

        return updateObject(prevState, { [itemKey]: updatedForm })
      })
    },
    [initiateGradeLevel]
  )

  const gradeLevelRowInputChangedHandler = useCallback(
    (gradeId, itemKey, event) => {
      setGradeLevelRows(prevState => {
        const targetGrades = [...prevState[currentLevelGroupId]]
        let targetGradeIndex = null
        let targetGrade = null
        if (targetGrades) {
          targetGradeIndex = targetGrades.findIndex(item => +item.grade_id.value === +gradeId)
          targetGrade = targetGrades[targetGradeIndex]
        }
        if (!targetGrade) {
          targetGrade = { ...gradeLevelRowTemplate }
        }

        const eventValue = event.target.value
        const lowerGradeIndex = targetGradeIndex + 1
        const higherGradeIndex = targetGradeIndex - 1
        let lowerGrade = targetGrades[lowerGradeIndex]
        let higherGrade = targetGrades[higherGradeIndex]

        if (itemKey === 'begin_score') {
          const targetEndScore = targetGrade.end_score
          // Min end score = this value + 0.01
          const minEndScore = parseFloat(eventValue) + 0.01 || Infinity

          let updatedEndScore = {
            ...targetEndScore,
            validation: { ...targetEndScore.validation, min: minEndScore }
          }

          updatedEndScore = updateFormData(updatedEndScore, {
            target: { value: updatedEndScore.value }
          })
          targetGrade = updateObject(targetGrade, { end_score: updatedEndScore })

          if (lowerGrade) {
            const lowerEndScore = lowerGrade.end_score
            // Max lower grade end score = this value - 0.01
            const maxLowerEndScore = parseFloat(eventValue) - 0.01 || -Infinity

            let updatedLowerEndScore = {
              ...lowerEndScore,
              validation: { ...lowerEndScore.validation, max: maxLowerEndScore },
              touched: true
            }

            updatedLowerEndScore = updateFormData(updatedLowerEndScore, {
              target: { value: updatedLowerEndScore.value }
            })
            lowerGrade = updateObject(lowerGrade, { end_score: updatedLowerEndScore })
          }
        }

        if (itemKey === 'end_score') {
          const targetBeginScore = targetGrade.begin_score
          // Max begin score = this value - 0.01
          const maxBeginScore = parseFloat(eventValue) - 0.01 || -Infinity

          let updatedBeginScore = {
            ...targetBeginScore,
            validation: { ...targetBeginScore.validation, max: maxBeginScore }
          }
          updatedBeginScore = updateFormData(updatedBeginScore, {
            target: { value: updatedBeginScore.value }
          })
          targetGrade = updateObject(targetGrade, { begin_score: updatedBeginScore })

          if (higherGrade) {
            const higherBeginScore = higherGrade.begin_score
            // Min higher begin score = this value + 0.01
            const minHigherBeginScore = parseFloat(eventValue) + 0.01 || Infinity

            let updatedHigherBeginScore = {
              ...higherBeginScore,
              validation: { ...higherBeginScore.validation, min: minHigherBeginScore },
              touched: true
            }

            updatedHigherBeginScore = updateFormData(updatedHigherBeginScore, {
              target: { value: updatedHigherBeginScore.value }
            })
            higherGrade = updateObject(higherGrade, { begin_score: updatedHigherBeginScore })
          }
        }

        const shouldValidateForm = Object.entries(targetGrade).some(formData => {
          const [formKey, formValue] = formData
          if (formKey === itemKey) {
            return !!eventValue
          }
          if (['static', 'hidden'].includes(formValue.elementType)) {
            return false
          }
          return !!formValue.value
        })

        const updatedGradeRow = Object.entries(targetGrade).reduce((updated, form) => {
          const [formKey, formItem] = form
          let updatedForm = { ...formItem }

          if (!['static', 'hidden'].includes(formItem.elementType)) {
            updatedForm = {
              ...updatedForm,
              validation: { ...updatedForm.validation, required: shouldValidateForm },
              touched: shouldValidateForm
            }
          }

          if (formKey === itemKey) {
            updatedForm = updateFormData(updatedForm, event)
          }

          return updateObject(updated, { [formKey]: updatedForm })
        }, {})

        const updatedGrades = targetGrades.map((item, index) => {
          if (item.grade_id.value === gradeId) {
            return updatedGradeRow
          } else if (index === higherGradeIndex) {
            return higherGrade
          } else if (index === lowerGradeIndex) {
            return lowerGrade
          }
          return item
        })

        return updateObject(prevState, { [currentLevelGroupId]: updatedGrades })
      })
    },
    [currentLevelGroupId]
  )

  // Filter Modal open handler
  const openFilterModalHandler = useCallback(() => {
    setFilterModalOpen(() => true)
  }, [])

  // Filter Modal close handler
  const closeFilterModalHandler = useCallback(() => {
    setFilterModalOpen(() => false)
  }, [])

  // Filter options changed handler
  const filterOptionChangedHandler = useCallback((itemKey, newOptions, { key, value, label }) => {
    setFilter(prevControls => {
      const filterItem = prevControls[itemKey]

      if (!filterItem) {
        return prevControls
      }

      const updatedFilter = updateFilterOptions(newOptions, filterItem, key, value, label)
      return updateObject(prevControls, { [itemKey]: updatedFilter })
    })
  }, [])

  // Filter input changed handler
  const filterInputChangedHandler = useCallback(
    (itemKey, event) => {
      setFilter(prevState => {
        const filterItem = prevState[itemKey]
        const updatedFilter = updateFilterData(filterItem, event)
        const updatedFilterControls = updateObject(prevState, { [itemKey]: updatedFilter })
        const { appraisal_type_id } = extractFilterOptions(updatedFilterControls)

        if (itemKey === 'appraisal_type_id') {
          if (appraisal_type_id) {
            dispatch(fetchCompanyOptions({ appraisal_type_id }))
          }
        }

        return updatedFilterControls
      })
    },
    [dispatch]
  )

  // Filter submitted handler
  const filterSubmittedHandler = useCallback(() => {
    const pagination = { page: 1, perPage: 10 }
    const filterOptions = extractFilterOptions(filter, { ...pagination })
    dispatch(fetchGrades(filterOptions))
    closeFilterModalHandler()
  }, [dispatch, filter, closeFilterModalHandler])

  // Pagination
  // Page Changed Handler
  const pageChangedHandler = useCallback(
    (e, page) => {
      const pageOptions = {
        perPage: pagination.perPage || 10,
        page: page || 1
      }

      const filterOptions = extractFilterOptions(filter, {
        ...pageOptions
      })

      dispatch(fetchGrades(filterOptions))
    },
    [dispatch, filter, pagination.perPage]
  )

  // Per Page Changed Handler
  const perPageChangedHandler = useCallback(
    perPage => {
      const pageOptions = {
        perPage: perPage || 10,
        page: pagination.currentPage || 1
      }

      const filterOptions = extractFilterOptions(filter, {
        ...pageOptions
      })

      dispatch(fetchGrades(filterOptions))
    },
    [dispatch, filter, pagination.currentPage]
  )

  // Confirmation Handlers
  const openConfirmationHandler = useCallback(deleteTargetId => {
    setConfirmModalOpen(true)
    setDeleteTargetId(deleteTargetId)
  }, [])

  const closeConfirmationHandler = useCallback(() => {
    setConfirmModalOpen(false)
    setDeleteTargetId(null)
  }, [])

  const confirmationConfirmed = useCallback(() => {
    if (deleteTargetId) {
      dispatch(deleteGrade(deleteTargetId))
    }
    closeConfirmationHandler()
  }, [dispatch, closeConfirmationHandler, deleteTargetId])

  const rowActions = [
    {
      icon: () => <Add />,
      tooltip: translate('add') + translate('gradeCriteria'),
      onClick: () => openEditModalHandler('add', null),
      isFreeAction: true,
      disabled: !permission.is_add
    },
    {
      icon: () => <Edit fontSize={'small'} />,
      tooltip: translate('edit') + translate('gradeCriteria'),
      onClick: (event, rowData) => openEditModalHandler('edit', rowData.grade_criteria_id)
      // disabled: !permission.is_edit
    },
    {
      icon: () => <Delete fontSize={'small'} />,
      tooltip: translate('delete') + translate('gradeCriteria'),
      onClick: (event, rowData) => openConfirmationHandler(rowData.grade_criteria_id),
      disabled: !permission.is_delete
    }
  ]

  const tableStructure = [
    { field: 'grade_criteria_name', title: translate('gradeCriteriaName'), type: 'string' },
    {
      field: 'company',
      title: translate('company'),
      type: 'string',
      render: rowData => rowData.company?.company_name || ''
    },
    { field: 'effective_date', title: translate('effectiveDate'), type: 'string' }
  ]

  // Mount Effects
  useEffect(() => {
    dispatch(fetchAppraisalTypeOptions())
    dispatch(fetchCompanyOptions())
    dispatch(fetchGrades({ page: 1, perPage: 10 }))
    return () => {}
  }, [dispatch])

  // Props Update Effects
  useEffect(() => {
    const optionConfig = filterLabelMapper.appraisal_type_id
    const optionMapper = {
      key: optionConfig.itemKey,
      value: optionConfig.itemValue,
      label: optionConfig.itemLabel
    }
    formOptionChangedHandler('appraisal_type_id', appraisalTypeOptions || [], optionMapper)
    filterOptionChangedHandler('appraisal_type_id', appraisalTypeOptions || [], optionMapper)
    return () => {
      formOptionChangedHandler('appraisal_type_id', [], optionMapper)
      filterOptionChangedHandler('appraisal_type_id', [], optionMapper)
    }
  }, [appraisalTypeOptions, filterOptionChangedHandler, formOptionChangedHandler])

  useEffect(() => {
    const optionConfig = filterLabelMapper.company_id
    const optionMapper = {
      key: optionConfig.itemKey,
      value: optionConfig.itemValue,
      label: optionConfig.itemLabel
    }
    formOptionChangedHandler('company_id', companyOptions || [], optionMapper)
    filterOptionChangedHandler('company_id', companyOptions || [], optionMapper)
    return () => {
      formOptionChangedHandler('company_id', [], optionMapper)
      filterOptionChangedHandler('company_id', [], optionMapper)
    }
  }, [companyOptions, filterOptionChangedHandler, formOptionChangedHandler])

  useEffect(() => {
    const optionConfig = filterLabelMapper.level_group_id
    const optionMapper = {
      key: optionConfig.itemKey,
      value: optionConfig.itemValue,
      label: optionConfig.itemLabel
    }
    const appraisalTypeId = form.appraisal_type_id.value?.value || null
    const companyId = form.company_id.value?.value || null

    if (appraisalTypeId && companyId) {
      additionalFormOptionChangedHandler('level_group_id', levelGroupOptions || [], optionMapper)
    }
    return () => {
      additionalFormOptionChangedHandler('level_group_id', [], optionMapper)
    }
  }, [
    levelGroupOptions,
    form.company_id.value,
    form.appraisal_type_id.value,
    additionalFormOptionChangedHandler
  ])

  // Filter Controls sync
  useEffect(() => {
    setFilter(prevControls => {
      const updatedControls = Object.entries(prevControls).reduce((updated, filterItem) => {
        const [filterKey, filterAttr] = filterItem
        const value = getSelectvalue(currentFilter?.[filterKey] || null, filterAttr.configs.options)
        const updatedAttr = { ...filterAttr, value }

        return updateObject(updated, { [filterKey]: updatedAttr })
      }, {})

      return updatedControls
    })
  }, [dispatch, currentFilter])

  // Open edit modal effect
  useEffect(() => {
    if (selectedRecord) {
      const { appraisal_type_id, company_id } = selectedRecord
      const gradeCriteriaLevel = selectedRecord.grade_level

      if (!editLevelGroupLoaded) {
        dispatch(fetchLevelGroupOptions({ appraisal_type_id, company_id }))
        setEditLevelGroupLoaded(true)
      }

      setForm(prevState => {
        const updatedForm = applyRecordDataToForm(prevState, selectedRecord)
        return updatedForm
      })

      if (gradeCriteriaLevel) {
        setGradeLevelRows(() => {
          const mappedData = Object.entries(gradeCriteriaLevel).reduce(
            (mapped, gradeLevelGroup) => {
              const [levelGroupId, gradeLevels] = gradeLevelGroup

              const mappedGradeLevel = gradeOptions.map(gradeItem => {
                const gradeLevelBase = {
                  grade_budget: 0,
                  begin_score: 0,
                  end_score: 0,
                  raise_percent: 0,
                  bonus_month: 0
                }
                const gradeLevel =
                  gradeLevels.find(item => +item.grade_id === +gradeItem.value_list_id) ||
                  gradeLevelBase
                const completeGradeLevel = {
                  ...gradeLevel,
                  level_group_id: +levelGroupId,
                  grade_id: +gradeItem?.value_list_id,
                  grade_name: gradeItem?.value_list_name
                }

                const buildedData = applyRecordDataToForm(gradeLevelRowTemplate, completeGradeLevel)
                // console.log(gradeItem.value_list_id, buildedData, completeGradeLevel)
                return buildedData
              })

              return updateObject(mapped, { [levelGroupId]: mappedGradeLevel })
            },
            {}
          )

          return mappedData
        })
      }
    }
  }, [dispatch, setEditLevelGroupLoaded, selectedRecord, editLevelGroupLoaded, gradeOptions])

  // Additional Form Effect (Enable level group id when all rows valid)
  useEffect(() => {
    setAdditionalForm(prevState => {
      const targetForm = prevState.level_group_id
      const value = prevState.level_group_id.value?.value || null
      const targetGradeLevelRows = gradeLevelRows[value]
      let currentLevelRowsValid = false
      let sumBudgetPercent = 0
      if (value && targetGradeLevelRows) {
        currentLevelRowsValid = validateGradeRows({ [value]: targetGradeLevelRows })
        sumBudgetPercent = (targetGradeLevelRows ?? []).reduce(
          (sum, item) => (sum += parseFloat(item.grade_budget.value)),
          0
        )
      }
      setShowPercentError(!isTrulyEqual(sumBudgetPercent, 100))
      const updatedForm = {
        ...targetForm,
        elementConfig: { ...targetForm.elementConfig, disabled: !currentLevelRowsValid }
      }
      return updateObject(prevState, { level_group_id: updatedForm })
    })
    return () => {}
  }, [gradeLevelRows, validateGradeRows])

  useEffect(() => {
    if (updateSuccess) {
      closeEditModalHandler()
    }
  }, [updateSuccess, closeEditModalHandler])

  return (
    <Fragment>
      <GradeCriteriaModal
        open={editModalOpen}
        submitDisabled={!permission.is_add || !permission.is_edit || !formIsValid}
        submittedHandler={formSubmittedHandler}
        cancelledHandler={closeEditModalHandler}
        form={form}
        formInputChanged={formInputChangedHandler}
        additionalForm={additionalForm}
        additionalFormInputChanged={additionalFormInputChangedHandler}
        gradeLevelRows={gradeLevelRows[currentLevelGroupId] ?? []}
        gradeLevelRowInputChanged={gradeLevelRowInputChangedHandler}
        percentError={showPercentError}
        translate={translate}
      />
      <Alert />
      <ConfirmationModal
        confirmed={confirmationConfirmed}
        cancelled={closeConfirmationHandler}
        open={confirmModalOpen}
        header={translate('gradeCriteria')}
        promptText={translate('confirmDel')}
      />
      <FilterModal
        open={filterModalOpen}
        filterControls={filter}
        filterChanged={filterInputChangedHandler}
        filterSaved={filterSubmittedHandler}
        cancelled={closeFilterModalHandler}
        loadingQueue={loadingQueue}
      />
      <GradeCriteriaContent
        tableTitle={translate('gradeCriteria')}
        tableStructure={tableStructure}
        rowActions={rowActions}
        openFilterModal={openFilterModalHandler}
        pagination={pagination}
        pageChanged={pageChangedHandler}
        perPageChanged={perPageChangedHandler}
        gradeList={gradeList}
        loadingStates={loadingQueue}
        translate={translate}
      />
    </Fragment>
  )
}

export default withTranslate(Grade)
